import React, { useState, useEffect } from "react";
import {
  Avatar,
  Divider,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

import { currency_list, convertCurrencyToSign } from "src/utils/constant";
import {
  _add_rate_for_client,
  _current_clients_list,
  _get_rate_of_client,
} from "src/DAL/team";
import { useAppContext } from "src/hooks";
import { CircularLoader, MuiDrawer } from "src/components";
import { s3baseUrl } from "src/config";
import { useSnackbar } from "notistack";

export default function PaymentDrawer({
  paymentDrawerOpen,
  setPaymentDrawerOpen,
  client,
  getClientRateList,
}) {
  const { dispatch_get_user_profile } = useAppContext();
  const [currentRow, setCurrentRow] = useState(undefined);
  const [saveLoading, setSaveLoading] = useState(false);

  const [priceLoading, setPriceLoading] = useState(false);
  const [paymentObject, setPaymentObject] = useState({
    workspaces: [],
    projects: [],
    over_all_price: "",
    over_all_currency: "",
  });

  const { enqueueSnackbar } = useSnackbar();

  const handleClosePaymentDrawer = () => {
    setPaymentDrawerOpen(false);
    setPaymentObject({
      workspaces: [],
      projects: [],
      over_all_price: "",
      over_all_currency: "",
    });
    setCurrentRow();
  };
  const handleAddRates = async () => {
    try {
      if (!currentRow) {
        enqueueSnackbar("No client selected", { variant: "error" });
        return;
      }
      setSaveLoading(true);
      let reqObj = {
        client_id: currentRow.user_id,
        admin_id: dispatch_get_user_profile().user_id,
        price_object: paymentObject,
      };
      const result = await _add_rate_for_client(reqObj);
      enqueueSnackbar(result.message, {
        variant: result.code == 200 ? "success" : "error",
      });
      if (result.code == 200) {
        handleClosePaymentDrawer();
        getClientRateList();
      }
    } catch (error) {
      console.log("Error catched while adding rates", error);
    } finally {
      setSaveLoading(false);
    }
  };

  const getRateOfClient = async () => {
    if (!currentRow) {
      enqueueSnackbar("No client selected", { variant: "error" });
      return;
    }
    try {
      const req = {
        client_id: currentRow.user_id,
        admin_id: dispatch_get_user_profile().user_id,
      };
      const result = await _get_rate_of_client(req);
      if (result.code == 200) {
        setPaymentObject(result.client_price.price_object);
      }
    } catch (error) {
      enqueueSnackbar("Something went wrong", { variant: "error" });
      console.log(error, " catched while getting client rate");
    } finally {
      setPriceLoading(false);
    }
  };

  useEffect(() => {
    if (paymentDrawerOpen) {
      setCurrentRow(client);
      console.log(client, "selected-client");
    }
  }, [paymentDrawerOpen]);

  useEffect(() => {
    if (currentRow) {
      getRateOfClient();
    }
  }, [currentRow]);
  return (
    <div>
      <MuiDrawer
        title={
          <Typography fontSize={14} fontWeight={700}>
           Client Currency and Rates
          </Typography>
        }
        isOpen={paymentDrawerOpen}
        onClose={handleClosePaymentDrawer}
        bottomComponent={
          !priceLoading && (
            <div className="d-flex justify-content-center mb-2 px-4">
              <LoadingButton
                fullWidth
                loading={saveLoading}
                variant="contained"
                onClick={handleAddRates}
              >
                Save
              </LoadingButton>
            </div>
          )
        }
      >
        {!priceLoading && (
          <Stack spacing={2}>
            <div className="d-flex align-items-center gap-2 p-1 mb-2">
              <Avatar
                src={s3baseUrl + client?.image}
                alt={client?.first_name}
                sx={{ width: 24, height: 24 }}
              />
              <Typography fontSize={13} fontWeight={500}>
                {client?.first_name + " " + client?.last_name}
              </Typography>
            </div>

            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">
                Choose Currency
              </InputLabel>
              <Select
                labelId="demo-simple-select-label"
                id="demo-simple-select"
                label="Choose Currency"
                value={paymentObject.over_all_currency}
                onChange={(e) => {
                  setPaymentObject((c) => {
                    return { ...c, over_all_currency: e.target.value };
                  });
                }}
              >
                {currency_list.map((c) => (
                  <MenuItem value={c.name}>{`${
                    c.sign
                  } ${c.name.toUpperCase()} `}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider>
              <Typography>Client Rate</Typography>
            </Divider>
            <FormControl>
              <InputLabel htmlFor="outlined-adornment-amount">
                Per hour rate
              </InputLabel>
              <OutlinedInput
                value={paymentObject.over_all_price}
                id="outlined-adornment-amount"
                startAdornment={
                  <InputAdornment position="start">
                    {convertCurrencyToSign(paymentObject.over_all_currency)}
                  </InputAdornment>
                }
                label="Per hour rate"
                onChange={(event) => {
                  const newValue = event.target.value;

                  if (/^$|^\d*\.?\d+$/.test(newValue)) {
                    setPaymentObject((prev) => {
                      return { ...prev, over_all_price: newValue };
                    });
                  }
                }}
              />
            </FormControl>

            <Divider>
              <Typography>Workspaces</Typography>
            </Divider>
            {currentRow &&
              currentRow.workspace.map((workspace) => (
                <Grid container spacing={0.5}>
                  <Grid item xs={6} className="d-flex align-items-center gap-2">
                    <Avatar
                      src={s3baseUrl + workspace.image}
                      sx={{
                        height: 20,
                        width: 20,
                      }}
                      alt={workspace.name}
                    />
                    <Typography>{workspace.name}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">
                        Per hour rate
                      </InputLabel>
                      <OutlinedInput
                        size="small"
                        value={
                          paymentObject.workspaces.find(
                            (space) => space._id == workspace._id
                          )?.price ?? ""
                        }
                        id="outlined-adornment-amount"
                        startAdornment={
                          <InputAdornment position="start">
                            {" "}
                            {convertCurrencyToSign(
                              paymentObject.over_all_currency
                            )}
                          </InputAdornment>
                        }
                        label="Per hour rate"
                        onChange={(event) => {
                          const newValue = event.target.value;

                          if (/^$|^\d*\.?\d+$/.test(newValue)) {
                            let found = paymentObject.workspaces.find(
                              (space) => space._id == workspace._id
                            );
                            if (found) {
                              setPaymentObject((prev) => {
                                return {
                                  ...prev,
                                  workspaces: prev.workspaces.map((space) => {
                                    if (space._id == workspace._id) {
                                      return {
                                        _id: space._id,
                                        price: newValue,
                                      };
                                    } else {
                                      return space;
                                    }
                                  }),
                                };
                              });
                            } else {
                              setPaymentObject((prev) => {
                                return {
                                  ...prev,
                                  workspaces: [
                                    ...prev.workspaces,
                                    { _id: workspace._id, price: newValue },
                                  ],
                                };
                              });
                            }
                          }
                        }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              ))}

            <Divider>
              <Typography>Projects</Typography>
            </Divider>
            {currentRow &&
              currentRow.project.length > 0 &&
              currentRow.project.map((project) => (
                <Grid container spacing={0.5}>
                  {/* <Grid item xs={2}>
                            <Checkbox />
                          </Grid> */}
                  <Grid item xs={6} className="d-flex align-items-center gap-2">
                    <Avatar
                      src={s3baseUrl + project.image}
                      sx={{
                        height: 20,
                        width: 20,
                      }}
                      alt={project.title}
                    />
                    <Typography>{project.title}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <FormControl fullWidth>
                      <InputLabel id="demo-simple-select-label">
                        Per hour rate
                      </InputLabel>
                      <OutlinedInput
                        size="small"
                        value={
                          paymentObject.projects.find(
                            (proj) => proj._id == project._id
                          )?.price ?? ""
                        }
                        id="outlined-adornment-amount"
                        startAdornment={
                          <InputAdornment position="start">
                            {" "}
                            {convertCurrencyToSign(
                              paymentObject.over_all_currency
                            )}
                          </InputAdornment>
                        }
                        label="Per hour rate"
                        onChange={(event) => {
                          const newValue = event.target.value;

                          if (/^$|^\d*\.?\d+$/.test(newValue)) {
                            let found = paymentObject.projects.find(
                              (proj) => proj._id == project._id
                            );
                            if (found) {
                              setPaymentObject((prev) => {
                                return {
                                  ...prev,
                                  projects: prev.projects.map((proj) => {
                                    if (proj._id == project._id) {
                                      return {
                                        _id: proj._id,
                                        price: newValue,
                                      };
                                    } else {
                                      return proj;
                                    }
                                  }),
                                };
                              });
                            } else {
                              setPaymentObject((prev) => {
                                return {
                                  ...prev,
                                  projects: [
                                    ...prev.projects,
                                    { _id: project._id, price: newValue },
                                  ],
                                };
                              });
                            }
                          }
                        }}
                      />
                    </FormControl>
                  </Grid>
                </Grid>
              ))}
            {currentRow && currentRow.project.length == 0 && (
              <Typography className="text-center" variant="subtitle">
                No projects for this client
              </Typography>
            )}
          </Stack>
        )}
        {priceLoading && <CircularLoader />}
      </MuiDrawer>
    </div>
  );
}
